import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Row,
  Col,
  Label,
  Button,
  FormFeedback
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select from "react-select";

import { selectCustomStyles } from "../../../common/selectCustomStyles";

const FeeTypeModal = ({
  modal,
  toggle,
  isEdit,
  feeType,
  prestations = [],
  branches = [],
  studyLevels = [],
  onSubmit
}) => {

  // State pour les niveaux filtrés
  const [filteredStudyLevels, setFilteredStudyLevels] = useState([]);

  // Formik
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: feeType?.id || "",
      uuid: feeType?.uuid || "",
      prestation_id: feeType?.prestation_id || null,
      nationality: feeType?.nationality || "",
      funding_source: feeType?.funding_source || null,
      branch_id: feeType?.branch_id || null,
      study_level_id: feeType?.study_level_id || null,
      amount: feeType?.amount || ""
    },
    validationSchema: Yup.object({
      prestation_id: Yup.object().nullable().required("Veuillez sélectionner la prestation"),
      nationality: Yup.string().required("Veuillez saisir la nationalité"),
      funding_source: Yup.object().nullable().required("Veuillez sélectionner la source de financement"),
      branch_id: Yup.object().nullable().required("Veuillez sélectionner la filière"),
      study_level_id: Yup.object().nullable().required("Veuillez sélectionner le niveau d'étude"),
      amount: Yup.number().required("Veuillez saisir le montant")
    }),
    onSubmit: values => {
      onSubmit({
        ...values,
        prestation_id: values.prestation_id.value,
        branch_id: values.branch_id.value,
        study_level_id: values.study_level_id.value,
        funding_source: values.funding_source.value,
        amount: Number(values.amount)
      });
    }
  });

  // Filtrage dynamique du niveau selon la filière
  useEffect(() => {
    if (!formik.values.branch_id) {
      setFilteredStudyLevels([]);
      formik.setFieldValue("study_level_id", null);
      return;
    }

    const filtered = studyLevels
      .filter(sl => sl.branch_id === formik.values.branch_id.value)
      .map(sl => ({ value: sl.id, label: sl.name }));

    setFilteredStudyLevels(filtered);

    // Reset si le niveau actuel ne correspond plus à la filière
    if (
      formik.values.study_level_id &&
      !filtered.some(sl => sl.value === formik.values.study_level_id.value)
    ) {
      formik.setFieldValue("study_level_id", null);
    }
  }, [formik.values.branch_id, studyLevels]);

  // Options formatées pour react-select
  const prestationOptions = prestations.map(p => ({ value: p.id, label: p.name }));
  const branchOptions = branches.map(b => ({ value: b.id, label: b.name }));
  const fundingOptions = [
    { value: "Parent", label: "Parent" },
    { value: "Bourse", label: "Bourse" }
  ];

  

  return (
    <Modal isOpen={modal} toggle={toggle} backdrop="static">
      <ModalHeader toggle={toggle}>
        {isEdit ? "Modifier - Type de frais" : "Ajouter - Type de frais"}
      </ModalHeader>

      <ModalBody>
        <form onSubmit={formik.handleSubmit}>
          <Row>
            <Col>

              {/* Prestation */}
              <div className="mb-3">
                <Label>Prestation</Label>
                <Select
                  options={prestationOptions}
                  value={formik.values.prestation_id}
                  onChange={value => formik.setFieldValue("prestation_id", value)}
                  onBlur={() => formik.setFieldTouched("prestation_id", true)}
                  placeholder="Sélectionner une prestation"
                  isSearchable
                  isClearable
                  menuPortalTarget={null}
                  styles={selectCustomStyles}
                  
                />
                {formik.touched.prestation_id && formik.errors.prestation_id && (
                  <div className="text-danger mt-1">{formik.errors.prestation_id}</div>
                )}
              </div>

              {/* Nationalité */}
              <div className="mb-3">
                <Label>Nationalité</Label>
                <input
                  type="text"
                  name="nationality"
                  placeholder="Nationalité"
                  className={`form-control ${formik.touched.nationality && formik.errors.nationality ? "is-invalid" : ""}`}
                  value={formik.values.nationality}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <FormFeedback>{formik.errors.nationality}</FormFeedback>
              </div>

              {/* Source de financement */}
              <div className="mb-3">
                <Label>Source de financement</Label>
                <Select
                  options={fundingOptions}
                  value={formik.values.funding_source}
                  onChange={value => formik.setFieldValue("funding_source", value)}
                  onBlur={() => formik.setFieldTouched("funding_source", true)}
                  placeholder="Sélectionner la source"
                  isSearchable
                  isClearable
                  menuPortalTarget={null}
                  styles={selectCustomStyles}
                />
                {formik.touched.funding_source && formik.errors.funding_source && (
                  <div className="text-danger mt-1">{formik.errors.funding_source}</div>
                )}
              </div>

              {/* Filière */}
              <div className="mb-3">
                <Label>Filière</Label>
                <Select
                  options={branchOptions}
                  value={formik.values.branch_id}
                  onChange={value => formik.setFieldValue("branch_id", value)}
                  onBlur={() => formik.setFieldTouched("branch_id", true)}
                  placeholder="Sélectionner la filière"
                  isSearchable
                  isClearable
                  menuPortalTarget={null}
                  styles={selectCustomStyles}
                />
                {formik.touched.branch_id && formik.errors.branch_id && (
                  <div className="text-danger mt-1">{formik.errors.branch_id}</div>
                )}
              </div>

              {/* Niveau d'étude */}
              <div className="mb-3">
                <Label>Niveau d'étude</Label>
                <Select
                  options={filteredStudyLevels}
                  value={formik.values.study_level_id}
                  onChange={value => formik.setFieldValue("study_level_id", value)}
                  onBlur={() => formik.setFieldTouched("study_level_id", true)}
                  placeholder="Sélectionner le niveau"
                  isSearchable
                  isClearable
                  menuPortalTarget={null}
                  styles={selectCustomStyles}
                  isDisabled={!formik.values.branch_id}
                />
                {formik.touched.study_level_id && formik.errors.study_level_id && (
                  <div className="text-danger mt-1">{formik.errors.study_level_id}</div>
                )}
              </div>

              {/* Montant */}
              <div className="mb-3">
                <Label>Montant</Label>
                <input
                  type="text"
                  name="amount"
                  placeholder="Montant"
                  className={`form-control ${formik.touched.amount && formik.errors.amount ? "is-invalid" : ""}`}
                  value={formik.values.amount}
                  //onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  onChange={(e) => {
                    // Autorise seulement chiffres et virgule
                    const value = e.target.value;
                    const numericValue = value.replace(/[^0-9,]/g, ""); 
                    formik.setFieldValue("amount", numericValue);
                }}
                />
                <FormFeedback>{formik.errors.amount}</FormFeedback>
              </div>

            </Col>
          </Row>

          <Row>
            <Col className="text-end">
              <Button type="submit" color="primary">
                {isEdit ? "Modifier" : "Enregistrer"}
              </Button>
            </Col>
          </Row>
        </form>
      </ModalBody>
    </Modal>
  );
};

export default FeeTypeModal;
